import { action, observable, computed, makeObservable } from 'mobx';

import type { EntryTypeValueData, EntryTypeValueTemplateData } from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';

import { SimpleList } from '@store/models/SimpleList';
import { moveItemInArray } from '@utils/moveItemInArray';
import { assertIsDefined } from '@utils/assertIsDefined';

interface GroupIdentityParams {
    // fieldName: string;
    templateId: string;
}

interface TemplateIdentityParams {
    templateId: string;
    itemId: string;
}

interface TemplateSubItemIdentityParams {
    fieldName: string;
    templateId: string;
    groupTemplateId: string;
    itemId: string;
}

export class EntryTypeFieldTemplateList extends SimpleList<EntryTypeValueData> {
    @observable private openTemplateIds = new Set<string>();

    constructor(params: EntryTypeValueData[] = []) {
        super(params);
        makeObservable(this);

        params.forEach(({ templates }) => {
            templates.forEach(({ id }) => {
                this.openTemplateIds.add(id);
            });
        });
    }

    @computed
    public get fieldNamesSet(): Set<string> {
        const filedNames = new Set<string>();
        this.values.forEach(({ fieldName }) => {
            if (fieldName) {
                filedNames.add(fieldName);
            }
        });

        return filedNames;
    }

    // Получить шаблоны наборы для полей
    @computed
    public get groupFieldTemplates(): EntryTypeValueData[] {
        return this.values.filter(({ name }) => !!name);
    }

    // Получить общие шаблоны для всех полей
    @computed
    public get commonFieldTemplates(): EntryTypeValueData[] {
        return this.values.filter(({ name }) => !name);
    }

    // Получить все шаблоны для филда
    public getFieldTemplates(fieldName: string): EntryTypeValueData[] {
        return this.values.filter(({ fieldName: currentFieldName }) => currentFieldName === fieldName);
    }

    // Получить общие шаблоны для филда (не имененые)
    public getFieldCommonGroup(fieldName: string): Optional<EntryTypeValueData> {
        return this.getFieldTemplates(fieldName).find(({ name }) => !name);
    }

    // Получить айдишник общего шаблоны для филда (не имененые)
    public getFieldCommonGroupId(fieldName: string): Optional<string> {
        return this.getFieldCommonGroup(fieldName)?.id;
    }

    public getFieldGroupById({ templateId }: GroupIdentityParams): EntryTypeValueData {
        return this.getExisting(templateId);
    }

    public getFieldGroupTemplates({ templateId }: GroupIdentityParams): EntryTypeValueTemplateData[] {
        return this.getFieldGroupById({ templateId }).templates.filter(
            ({ subValues }) => (subValues || []).length === 0,
        );
    }

    public getFieldGroupSubGroups({ templateId }: GroupIdentityParams): EntryTypeValueTemplateData[] {
        return this.getFieldGroupById({ templateId }).templates.filter(({ subValues }) => (subValues || []).length > 0);
    }

    public getFieldGroupTemplateById({ itemId, ...rest }: TemplateIdentityParams): EntryTypeValueTemplateData {
        const template = this.getFieldGroupById(rest).templates.find(({ id }) => itemId === id);
        assertIsDefined(template, `Template ${itemId} is picked from the field group templates`);
        return template;
    }

    public getFieldGroupSubGroup({ itemId, templateId }: TemplateIdentityParams): EntryTypeValueTemplateData {
        const subGroup = this.getFieldGroupSubGroups({ templateId }).find(({ id }) => id === itemId);
        assertIsDefined(subGroup, `Sub group ${itemId} is picked from the field group sub groups`);
        return subGroup;
    }

    public getFieldGroupSubGroupsTemplates({
        itemId,
        templateId,
    }: TemplateIdentityParams): EntryTypeValueTemplateData[] {
        const { subValues } = this.getFieldGroupSubGroup({ templateId, itemId });
        assertIsDefined(subValues, `Sub group ${itemId} is picked among sub groups with templates`);
        return subValues;
    }

    public isNeedChangeIndexItemIndex(
        { itemId, templateId }: Omit<TemplateIdentityParams, 'fieldName'>,
        newIndex: number,
    ): boolean {
        const { templates } = this.getExisting(templateId);

        const currentIndex = templates.findIndex(({ id }) => id === itemId);
        const toIndex = this.getToIndex(currentIndex, newIndex);

        return currentIndex !== toIndex;
    }

    public updateTemplateItemPosition(
        { itemId, templateId }: Omit<TemplateIdentityParams, 'fieldName'>,
        newIndex: number,
    ): void {
        const { templates } = this.getExisting(templateId);

        const currentIndex = templates.findIndex(({ id }) => id === itemId);
        const toIndex = this.getToIndex(currentIndex, newIndex);

        if (currentIndex !== toIndex) {
            const updated = moveItemInArray(templates, currentIndex, toIndex);
            this.update(templateId, { templates: updated });
        }
    }

    @action
    public addTemplate(id: string, params: EntryTypeValueTemplateData): void {
        const { templates } = this.getExisting(id);
        this.update(id, { templates: [...templates, params] });

        this.openTemplateIds.add(id);
    }

    @action
    public addTemplates(id: string, params: EntryTypeValueTemplateData[]): void {
        const { templates } = this.getExisting(id);
        this.update(id, { templates: [...templates, ...params] });

        this.openTemplateIds.add(id);
    }

    @action
    public updateTemplateName(templateId: string, name: string): void {
        this.update(templateId, { name });
    }

    @action
    public updateTemplateValue(
        { templateId, itemId }: Omit<TemplateIdentityParams, 'fieldName'>,
        newValue: string,
    ): void {
        const { templates } = this.getExisting(templateId);

        const newTemplates = templates.map(template =>
            template.id === itemId ? { ...template, value: newValue } : template,
        );

        this.update(templateId, { templates: newTemplates });
    }

    @action
    public removeTemplate(templateId: string): void {
        this.remove(templateId);
    }

    @action
    public resetGroupTemplateToCommon(templateId: string): void {
        const groupTemplate = this.getExisting(templateId);
        assertIsDefined(groupTemplate.fieldName, `Group template ${templateId} is created for a field`);
        const commonGroup = this.getFieldCommonGroup(groupTemplate.fieldName);
        if (!commonGroup) {
            this.update(templateId, { name: undefined });
        } else {
            this.addTemplates(commonGroup.id, groupTemplate.templates);
        }
    }

    @action
    public addTemplateSubItem(
        { itemId, templateId }: TemplateIdentityParams,
        params: EntryTypeValueTemplateData,
    ): void {
        const { templates } = this.getExisting(templateId);
        const newTemplates = templates.map(template =>
            template.id === itemId ? { ...template, subValues: [...(template.subValues || []), params] } : template,
        );

        this.update(templateId, { templates: newTemplates });
    }

    @action
    public removeTemplateItem({ templateId, itemId }: Omit<TemplateIdentityParams, 'fieldName'>): void {
        const { templates } = this.getExisting(templateId);
        const newTemplates = templates.filter(template => template.id !== itemId);

        this.update(templateId, { templates: newTemplates });
    }

    @action
    public updateTemplateItemValue(
        { fieldName, groupTemplateId, templateId, itemId }: TemplateSubItemIdentityParams,
        newValue: string,
    ): void {
        const { templates } = this.getExisting(templateId);

        const newTemplates = templates.map(template =>
            template.id === groupTemplateId
                ? {
                      ...template,
                      subValues: (template.subValues || []).map(subValue =>
                          subValue.id === itemId ? { ...subValue, value: newValue } : subValue,
                      ),
                  }
                : template,
        );

        this.update(templateId, { templates: newTemplates });
    }

    private getToIndex(currentIndex: number, newIndex: number): number {
        return newIndex > currentIndex ? newIndex - 1 : newIndex;
    }
}
